/**
 * Shared helpers for ticket log rows and cards (TicketLogRow / TicketCard).
 */

import { createNavigationHandler } from "../../../utils/common/navigation";

/**
 * Whether an event originated from row/card action controls (kebab menu,
 * custom actions). Those clicks should never open the ticket.
 *
 * @param {React.SyntheticEvent} e - Mouse/click event.
 * @returns {boolean} True when the event should not open the ticket.
 */
export const isTicketActionsTarget = (e) =>
  Boolean(
    e.target.closest(".dropdown-actions-container") ||
      e.target.closest(".custom-actions-container")
  );

/**
 * Build the activation handler shared by ticket rows and cards.
 *
 * Left click calls ``onActivate``; when ``getRowHref`` supplies an app path,
 * middle click and Ctrl/Cmd+click open the ticket in a new tab. Attach the
 * returned handler to ``onClick``, ``onMouseDown``, and ``onAuxClick``.
 *
 * @param {Object} item - The ticket/item data object.
 * @param {Function} [getRowHref] - Optional app path builder: (item) => string.
 * @param {Function} [onActivate] - Left-click open handler: (item) => void.
 * @returns {(e: React.SyntheticEvent) => void} Event handler.
 */
export function createTicketActivationHandler(item, getRowHref, onActivate) {
  const href = typeof getRowHref === "function" ? getRowHref(item) : null;
  const navHandler = href
    ? createNavigationHandler(href, () => onActivate?.(item))
    : null;

  return (e) => {
    if (isTicketActionsTarget(e)) return;
    if (navHandler) {
      navHandler(e);
      return;
    }
    // No href: only respond to real clicks (skip mousedown/auxclick passes).
    if (e.type === "click") {
      onActivate?.(item);
    }
  };
}
